/**
 * RawSearchStream — raw FYC search feed.
 *
 * Consumes GET /api/admin/dashboard/analytics/church-demand/searches via
 * churchDemandAPI.searches(params). Server-side filtered to source_type=
 * 'fyc_search' — this is the literal stream of what people typed into the
 * Find Your Church search bar.
 *
 * Date range filters are date-only (type="date"); we append the time portion
 * in UTC to build ISO timestamps the backend expects.
 */

import { useEffect, useState } from 'react';
import { churchDemandAPI } from '../../../services/api';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function dateToFromIso(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  return `${yyyyMmDd}T00:00:00Z`;
}

function dateToToIso(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  return `${yyyyMmDd}T23:59:59Z`;
}

export default function RawSearchStream() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [searches, setSearches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inline date-range validation before we even hit the backend
  const dateRangeInvalid = fromDate && toDate && fromDate > toDate;

  useEffect(() => {
    if (dateRangeInvalid) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = { limit: PAGE_SIZE, offset };
        const fromIso = dateToFromIso(fromDate);
        const toIso = dateToToIso(toDate);
        if (fromIso) params.from = fromIso;
        if (toIso) params.to = toIso;

        const res = await churchDemandAPI.searches(params);
        if (cancelled) return;
        setSearches(res.data?.searches || []);
        setTotal(res.data?.total || 0);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Failed to load searches'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, offset, dateRangeInvalid]);

  const applyDateChange = (setter) => (value) => {
    setter(value);
    setOffset(0);
  };

  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = Math.min(offset + PAGE_SIZE, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              From
            </span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => applyDateChange(setFromDate)(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-h-[36px]"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              To
            </span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => applyDateChange(setToDate)(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-h-[36px]"
            />
          </label>
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setOffset(0);
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 min-h-[36px]"
            >
              Clear
            </button>
          )}
        </div>
        {dateRangeInvalid && (
          <p className="mt-2 text-sm text-red-600">
            <strong>Invalid range:</strong> "From" must be on or before "To".
          </p>
        )}
      </div>

      {/* Error banner */}
      {error && !dateRangeInvalid && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
          <strong className="font-semibold">Failed to load:</strong> {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Raw query
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Derived Geo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                    Loading searches…
                  </td>
                </tr>
              )}
              {!loading && searches.length === 0 && !dateRangeInvalid && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                    No searches match the current filters.
                  </td>
                </tr>
              )}
              {!loading &&
                searches.map((s) => (
                  <tr key={s.source_id}>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(s.contributed_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {s.raw_name || '(empty)'}
                    </td>
                    <td className="px-4 py-3">
                      {s.user_name || s.user_email ? (
                        <div className="space-y-0.5">
                          {s.user_name && (
                            <div className="text-sm text-gray-900">{s.user_name}</div>
                          )}
                          {s.user_email && (
                            <div className="text-xs text-gray-500">{s.user_email}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">anonymous</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {s.derived_city || s.derived_state ? (
                        [s.derived_city, s.derived_state].filter(Boolean).join(', ')
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {showingStart}–{showingEnd} of {total}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!hasPrev}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 min-h-[36px]"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasNext}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 min-h-[36px]"
          >
            Next
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
